<script setup lang="ts">
const { setBreadcrumb } = useLsBreadcrumb()
import {
  getBlackPlatformRanking,
  invokeExposureRoute,
  type ExposureStatisticsResponse,
  normalizeExposureStatisticsData,
} from '~/api/exposure'

type SidebarStat = {
  label: string
  value: string
}

type NegativeReviewItem = {
  name: string
  logo: string
  code: string
}

type ExposureShellPageData = {
  sidebarStats: SidebarStat[]
  mediationStats: SidebarStat[]
  negativeReviewItems: NegativeReviewItem[]
  negativeReviewTitle: string
}

const EXPOSURE_SEARCH_QUERY_KEY = 'exposureKeyword'

const props = withDefaults(
  defineProps<{
    activeSection?: 'reviews' | 'mediation'
    showMediationTab?: boolean
    showBottomPagination?: boolean
  }>(),
  {
    activeSection: undefined,
    showMediationTab: true,
    showBottomPagination: true,
  },
)

/** 当前路由用于切换顶部页签的高亮态。 */
const route = useRoute()
const { t, locale } = useAppI18n()
const localePath = useLocalePath()
const matomo = useMatomo()

/** 根据子路由目录识别当前展示的是哪个曝光模块。 */
const currentSection = computed(() => {
  if (props.activeSection) {
    return props.activeSection
  }

  return route.path.includes('/reviews') ? 'reviews' : 'mediation'
})

/** 暴光频道顶部页签。 */
const primaryTabs = computed(() => [
  {
    key: 'reviews',
    label: t('84695'),
    to: localePath('/reviews'),
  },
  ...(props.showMediationTab
    ? [
        {
          key: 'mediation',
          label: t('83641'),
          to: localePath('/mediation'),
        },
      ]
    : []),
])

/** 仅在存在多个顶部页签时显示当前选中态横杠。 */
const showPrimaryTabIndicator = computed(() => primaryTabs.value.length > 1)

/** 当前选中的曝光类型，默认全部。 */
const currentCategoryCode = computed(() => {
  const categoryParam = route.params.category || route.query.c
  const resolvedCode = Array.isArray(categoryParam) ? categoryParam[0] : categoryParam

  return typeof resolvedCode === 'string' && resolvedCode.trim() ? resolvedCode.trim() : '0'
})

/** URL 上的搜索词，优先使用路由参数。 */
const currentKeyword = computed(() => {
  const searchQuery = route.query[EXPOSURE_SEARCH_QUERY_KEY]
  const resolvedKeyword = Array.isArray(searchQuery) ? searchQuery[0] : searchQuery

  return typeof resolvedKeyword === 'string' ? resolvedKeyword.trim() : ''
})

/** 搜索框输入值与路由关键词保持同步。 */
const searchKeyword = ref('')
const feedbackDialogVisible = ref(false)
const feedbackButtonBottom = ref('120px')
/** 页脚高度用于计算曝光页主容器最小高度，避免内容较少时页面过早收尾。 */
const footerHeight = ref(315)

watch(
  currentKeyword,
  (keyword) => {
    searchKeyword.value = keyword
  },
  {
    immediate: true,
  },
)

/** 从当前路由参数（page 或 slug）中提取纯数字页码，统一给曝光和 reviews 子页面复用。 */
const currentPage = computed(() => {
  const pageValue = route.params.page || route.params.slug
  const pageSlug = Array.isArray(pageValue) ? pageValue[0] : pageValue
  const parsedPage = Number(pageSlug || '1')

  if (!Number.isFinite(parsedPage) || parsedPage < 1) {
    return 1
  }

  return Math.trunc(parsedPage)
})

/** 黑榜列表为空时隐藏整块侧栏模块。 */
const hasNegativeReviewItems = computed(() => exposurePageData.value.negativeReviewItems.length > 0)

/** 曝光页公共面包屑。 */
const breadcrumbItems = computed(() => {
  const baseRoute = '/reviews'
  const channelLabel = t('84695')

  const items: { label: string; route?: string }[] = [
    {
      label: t('82520'),
      route: '/',
    },
  ]

  const hasSearch = currentKeyword.value !== ''

  if (hasSearch) {
    items.push({
      label: channelLabel,
      route: baseRoute,
    })
    items.push({
      label: t('84608'),
    })
  } else {
    items.push({
      label: channelLabel,
    })
  }

  return items
})

/** 提交搜索后统一回到第一页，整页重新走 SSR 渲染。 */
const submitSearch = async () => {
  const keyword = searchKeyword.value.trim()

  matomo.trackEvent('af_Exposure_In_Search', { af_content: keyword })

  const query: Record<string, string> = {}

  const hasSearch = keyword !== ''
  if (hasSearch) {
    query[EXPOSURE_SEARCH_QUERY_KEY] = keyword
  }

  let targetPath = '/reviews'

  if (currentCategoryCode.value === '0') {
    targetPath = hasSearch ? '/reviews/search' : '/reviews'
  } else {
    targetPath = hasSearch
      ? `/reviews/category/${currentCategoryCode.value}/search`
      : `/reviews/category/${currentCategoryCode.value}`
  }

  await navigateTo({
    path: localePath(targetPath),
    query,
  })
}

/** 仅清除搜索框文本，不触发路由、请求或埋点。 */
const clearSearch = () => {
  searchKeyword.value = ''
}

const EMPTY_EXPOSURE_SHELL_PAGE_DATA: ExposureShellPageData = {
  sidebarStats: [],
  mediationStats: [],
  negativeReviewItems: [],
  negativeReviewTitle: '',
}
const EXPOSURE_SHELL_ROUTES = 'wikifxforum$wikifxforum_statistics'

/** 曝光页接口统一在父层并发调用，再下发给子组件复用。 */
const { data: exposurePageData, pending: exposurePagePending } = await useAsyncData(
  `exposure-page-${locale}-${currentSection.value}`,
  async () => {
    try {
      const [response, weekRankings] = await Promise.all([
        invokeExposureRoute<ExposureStatisticsResponse>(EXPOSURE_SHELL_ROUTES, { userid: '' }),
        getBlackPlatformRanking(),
      ])
      const statistics = normalizeExposureStatisticsData(response as ExposureStatisticsResponse)
      const currencyCode = `${statistics?.currency_code || ''}`.trim()
      const amountText = `${statistics?.three_month_amount || ''}`.trim()
      const resolvedAmountValue = currencyCode ? amountText.replace(currencyCode, '').trim() : amountText // 近一个月已解决金额
      const totalAmountText = `${statistics?.amount || ''}`.trim()
      const resolvedTotalAmountValue = currencyCode ? totalAmountText.replace(currencyCode, '').trim() : totalAmountText // 总金额
      const resolvedPeopleValue = `${statistics?.count ?? ''}`.trim() // 已解决人数
      const sidebarStats: SidebarStat[] = []

      if (amountText) {
        sidebarStats.push({
          label: t('83858'),
          value: resolvedTotalAmountValue,
        })
      }

      sidebarStats.push({
        label: `${t('84549')}`,
        value: resolvedPeopleValue,
      })

      const mediationStats: SidebarStat[] = [
        {
          label: t('83842'),
          value: resolvedAmountValue,
        },
        {
          label: t('83843'),
          value: `${statistics?.speed ?? ''}`.trim(),
        },
      ]

      const rankingList = Array.isArray(weekRankings)
        ? weekRankings
        : Array.isArray(weekRankings?.list)
          ? weekRankings.list
          : Array.isArray(weekRankings?.weekRankings)
            ? weekRankings.weekRankings
            : Array.isArray(weekRankings?.data?.result?.weekRankings)
              ? weekRankings.data.result.weekRankings
              : Array.isArray(weekRankings?.data?.weekRankings)
                ? weekRankings.data.weekRankings
                : Array.isArray(weekRankings?.data?.traderRank)
                  ? weekRankings.data.traderRank
                  : []
      const totalRankingList = Array.isArray(weekRankings?.data?.result?.totalRankings)
        ? weekRankings.data.result.totalRankings
        : Array.isArray(weekRankings?.totalRankings)
          ? weekRankings.totalRankings
          : Array.isArray(weekRankings?.data?.totalRankings)
            ? weekRankings.data.totalRankings
            : []
      const normalizedWeekRankingItems = rankingList
        .map((item) => {
          const name = `${item?.localShortName || item?.name || ''}`.trim()
          const logo = `${item?.logo || item?.ico || item?.icon || ''}`.trim()
          const code = `${item?.alias || item?.traderCode || item?.code || ''}`.trim()

          if (!name) {
            return null
          }

          return {
            name,
            logo,
            code,
          }
        })
        .filter((item): item is NegativeReviewItem => item !== null)
      const normalizedTotalRankingItems = totalRankingList
        .map((item) => {
          const name = `${item?.localShortName || item?.name || ''}`.trim()
          const logo = `${item?.logo || item?.ico || item?.icon || ''}`.trim()
          const code = `${item?.alias || item?.traderCode || item?.code || ''}`.trim()

          if (!name) {
            return null
          }

          return {
            name,
            logo,
            code,
          }
        })
        .filter((item): item is NegativeReviewItem => item !== null)
      const useWeekRankingItems = normalizedWeekRankingItems.length >= 3
      const negativeReviewItems = useWeekRankingItems ? normalizedWeekRankingItems : normalizedTotalRankingItems

      return {
        sidebarStats,
        mediationStats,
        negativeReviewItems,
        negativeReviewTitle: useWeekRankingItems ? t('83859') : t('84607'),
      }
    } catch {
      return {
        sidebarStats: EMPTY_EXPOSURE_SHELL_PAGE_DATA.sidebarStats,
        mediationStats: EMPTY_EXPOSURE_SHELL_PAGE_DATA.mediationStats,
        negativeReviewItems: EMPTY_EXPOSURE_SHELL_PAGE_DATA.negativeReviewItems,
        negativeReviewTitle: EMPTY_EXPOSURE_SHELL_PAGE_DATA.negativeReviewTitle,
      }
    }
  },
  {
    default: () => ({
      sidebarStats: EMPTY_EXPOSURE_SHELL_PAGE_DATA.sidebarStats,
      mediationStats: EMPTY_EXPOSURE_SHELL_PAGE_DATA.mediationStats,
      negativeReviewItems: EMPTY_EXPOSURE_SHELL_PAGE_DATA.negativeReviewItems,
      negativeReviewTitle: EMPTY_EXPOSURE_SHELL_PAGE_DATA.negativeReviewTitle,
    }),
    server: true,
  },
)

/** 根据 footer 与当前视口的位置，动态调整反馈按钮底部间距。 */
const updateFeedbackButtonBottom = () => {
  if (!import.meta.client) {
    return
  }

  const footerElement = document.querySelector<HTMLElement>('footer.wiki-bit-pc-bootom-map')

  if (!footerElement) {
    feedbackButtonBottom.value = '120px'
    return
  }

  const footerRect = footerElement.getBoundingClientRect()
  const bottomOffset = Math.max(120, window.innerHeight - footerRect.top + 120)

  feedbackButtonBottom.value = `${Math.round(bottomOffset)}px`
}

let feedbackButtonFrameId: number | null = null

/** 使用动画帧节流，避免滚动时重复计算按钮位置。 */
const scheduleUpdateFeedbackButtonBottom = () => {
  if (!import.meta.client || feedbackButtonFrameId !== null) {
    return
  }

  feedbackButtonFrameId = window.requestAnimationFrame(() => {
    feedbackButtonFrameId = null
    updateFeedbackButtonBottom()
  })
}

onMounted(() => {
  updateFeedbackButtonBottom()
  const footerElement = document.querySelector<HTMLElement>('footer.wiki-bit-pc-bootom-map')

  if (footerElement) {
    footerHeight.value = footerElement.offsetHeight
  }

  window.addEventListener('scroll', scheduleUpdateFeedbackButtonBottom, { passive: true })
  window.addEventListener('resize', scheduleUpdateFeedbackButtonBottom)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', scheduleUpdateFeedbackButtonBottom)
  window.removeEventListener('resize', scheduleUpdateFeedbackButtonBottom)

  if (feedbackButtonFrameId !== null) {
    window.cancelAnimationFrame(feedbackButtonFrameId)
    feedbackButtonFrameId = null
  }
})

/** 点击新开详情页前缓存当前列表页，供详情页还原第二级面包屑。 */
const lsBreadcrumb = (item) => {
  setBreadcrumb(item.code as string, {
    labelKey: '84695', // 走翻译的值
    route: route.fullPath,
  })
}
</script>

<template>
  <section class="bg-[#ffffff]">
    <div
      class="mx-auto flex max-w-[1248px] flex-col px-[24px] pt-[70px] pb-[80px]"
      :style="{ minHeight: `calc(100vh - ${footerHeight}px)` }"
    >
      <CommonBreadcrumb :items="breadcrumbItems" />

      <div class="grid grid-cols-[minmax(0,789px)_379px] gap-[24px] max-[1199px]:grid-cols-[minmax(0,1fr)]">
        <div class="min-w-0">
          <div class="sticky top-[70px] z-[3] mb-[24px] bg-[#ffffff]">
            <div class="flex items-center gap-[24px]">
              <div class="flex min-w-0 flex-1 gap-[24px] border-b border-[rgba(5,21,36,0.06)]">
                <template v-for="item in primaryTabs" :key="item.key">
                  <span
                    v-if="currentSection === item.key"
                    class="relative flex h-[60px] shrink-0 items-center pb-[4px] text-[20px] leading-[28px] font-[600] text-[#292a2e]"
                  >
                    {{ item.label }}
                    <span
                      v-if="showPrimaryTabIndicator"
                      class="absolute bottom-0 left-1/2 h-[4px] w-[28px] -translate-x-1/2 rounded-full bg-[#292a2e]"
                    ></span>
                  </span>

                  <NuxtLink
                    v-else
                    :to="item.to"
                    target="_blank"
                    class="flex h-[60px] shrink-0 items-center text-[20px] leading-[28px] font-[400] text-[#7d818a] transition-colors hover:text-[#292a2e]"
                  >
                    {{ item.label }}
                  </NuxtLink>
                </template>
              </div>
            </div>
          </div>

          <div class="">
            <slot />
          </div>
        </div>

        <!-- 调解数据 -->
        <aside
          class="sticky top-[70px] flex flex-col gap-[24px] self-start pt-[9px] max-[1199px]:static max-[1199px]:w-full"
        >
          <div class="w-[379px] rounded-[999px] bg-[#f8f8f8] px-[16px] py-[12px] max-[1199px]:w-full">
            <label class="flex items-center gap-[4px] text-[#7d818a]">
              <button
                type="button"
                class="flex size-[20px] shrink-0 cursor-pointer items-center justify-center rounded-full text-[#7d818a] transition-colors hover:text-[#292a2e]"
                @click="submitSearch"
              >
                <span class="sr-only">{{ t('83861') }}</span>
                <svg class="size-[16px]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path
                    d="M14.5833 14.5833L18.3333 18.3333M16.6667 8.75C16.6667 13.1223 13.1223 16.6667 8.75 16.6667C4.37775 16.6667 0.833332 13.1223 0.833332 8.75C0.833332 4.37775 4.37775 0.833333 8.75 0.833333C13.1223 0.833333 16.6667 4.37775 16.6667 8.75Z"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <input
                v-model="searchKeyword"
                type="text"
                :placeholder="t('83861')"
                class="h-[22px] flex-1 border-[0px] bg-transparent text-[14px] leading-[22px] text-[#292a2e] outline-none placeholder:text-[#7d818a]"
                @keydown.enter="submitSearch"
              />
              <button
                v-if="searchKeyword.trim()"
                type="button"
                class="flex size-[20px] shrink-0 cursor-pointer appearance-none items-center justify-center rounded-full border-0 p-0 text-[#8c8f97] shadow-none transition-colors"
                aria-label="Clear search"
                @click="clearSearch"
              >
                <CommonImage
                  src="https://resources1.interface003.com/web30/2026/07/13/69df0e6d-0a9d-4a63-a745-007906bc6eec.svg"
                  alt=""
                  width="20"
                  height="20"
                  loading="eager"
                  object-fit="contain"
                  aria-hidden="true"
                />
              </button>
            </label>
          </div>

          <section
            v-if="showMediationTab"
            class="rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-[#ffffff] px-[24px] pt-[8px] pb-[16px]"
          >
            <div class="border-b border-[rgba(5,21,36,0.06)] py-[16px]">
              <h3 class="text-[20px] leading-[28px] font-[600] text-[#292a2e]">{{ t('83862') }}</h3>
            </div>

            <div class="flex flex-col gap-[12px] pt-[12px]">
              <div
                v-for="item in exposurePageData.sidebarStats.slice(0, 2)"
                :key="item.label"
                class="flex flex-col gap-[2px]"
              >
                <div class="text-[13px] leading-[20px] text-[#8c8f97]">{{ item.label }}</div>
                <div class="text-[24px] leading-[32px] font-[700] tracking-[0] text-[#2546a8]">
                  {{ item.value }}
                </div>
              </div>
            </div>
          </section>

          <!-- 调解进展 -->
          <section
            v-if="showMediationTab && currentSection === 'mediation'"
            class="rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-[#ffffff] px-[24px] pt-[8px] pb-[16px]"
          >
            <div class="border-b border-[rgba(5,21,36,0.06)] py-[16px]">
              <h3 class="text-[20px] leading-[28px] font-[600] text-[#292a2e]">{{ t('83841') }}</h3>
            </div>

            <div class="flex flex-col gap-[12px] pt-[12px]">
              <div v-for="item in exposurePageData.mediationStats" :key="item.label" class="flex flex-col gap-[2px]">
                <div class="text-[13px] leading-[20px] text-[#8c8f97]">{{ item.label }}</div>
                <div class="text-[24px] leading-[32px] font-[700] tracking-[0] text-[#2546a8]">
                  {{ item.value }}
                </div>
              </div>
            </div>
          </section>

          <!-- 本周最多差评 -->
          <section
            v-if="currentSection === 'reviews' && hasNegativeReviewItems"
            class="rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-[#ffffff] px-[24px] pt-[8px] pb-[16px]"
          >
            <div class="border-b border-[rgba(5,21,36,0.06)] py-[16px]">
              <h3 class="text-[20px] leading-[28px] font-[600] text-[#292a2e]">
                {{ exposurePageData.negativeReviewTitle }}
              </h3>
            </div>

            <div class="pt-[12px]">
              <template v-for="(item, index) in exposurePageData.negativeReviewItems" :key="`${item.name}-${index}`">
                <NuxtLink
                  v-if="item.code"
                  :to="
                    localePath({
                      name: 'exchange-detail',
                      params: { slug: item.code },
                    })
                  "
                  target="_blank"
                  class="group flex h-[46px] cursor-pointer items-center gap-[8px] rounded-[4px] transition-colors hover:bg-[#F8F8F8]"
                  @click="lsBreadcrumb(item)"
                >
                  <span
                    class="flex w-[20px] shrink-0 items-center justify-center text-[14px] leading-[22px]"
                    :class="index < 3 ? 'text-[#292a2e]' : 'text-[#b7b9be]'"
                  >
                    {{ index + 1 }}
                  </span>
                  <div class="flex space-x-[4px]">
                    <CommonImage
                      class="!block h-[20px] w-[20px] rounded-[2px]"
                      :src="item.logo"
                      alt=""
                      loading="eager"
                      object-fit="cover"
                    />
                  </div>
                  <span class="min-w-0 truncate text-[16px] leading-[24px] text-[#292a2e] transition-colors">
                    {{ item.name }}
                  </span>
                </NuxtLink>
              </template>
            </div>
          </section>
        </aside>
      </div>

      <div id="reviews-pagination-target" class="contents"></div>
      <div
        class="fixed right-[max(24px,calc((100vw-1248px)/2+24px))] z-[20] flex justify-center rtl:right-auto rtl:left-[max(24px,calc((100vw-1248px)/2+24px))]"
        :style="{ bottom: feedbackButtonBottom }"
      >
        <button
          type="button"
          class="write-feedback-button relative inline-flex h-[54px] min-w-[190px] shrink-0 cursor-pointer items-center justify-center gap-[8px] overflow-visible rounded-[1000px] border-0 bg-[#112d73] px-[32px] py-[16px] text-center text-[14px] leading-[22px] font-[600] whitespace-nowrap text-white shadow-[0_0_1px_0_rgba(30,31,33,0.12),0_0_8px_0_rgba(30,31,33,0.16)] transition-colors duration-200"
          :aria-label="t('83575')"
          @click="feedbackDialogVisible = true"
        >
          <span class="flex shrink-0 items-center justify-center" aria-hidden="true">
            <CommonImage
              class="h-[20px] w-[20px] shrink-0 brightness-0 invert"
              src="https://resources1.interface003.com/web30/2026/07/03/f69ef16a-95b2-4a8a-a554-c5103bc09ce9.svg"
              alt=""
            />
          </span>
          <span class="shrink-0">{{ t('83575') }}</span>
        </button>
      </div>
    </div>

    <ExchangeDetailExchangeFeedbackDialog
      :show="feedbackDialogVisible"
      :allow-exchange-search="true"
      @close="feedbackDialogVisible = false"
      @example="feedbackDialogVisible = false"
    />
  </section>
</template>

<style scoped lang="less">
/* 分页激活态覆盖：#112D73 */
:deep(.inline-flex.items-center.justify-start) {
  button[aria-current='page'] {
    background-color: #112d73;
    color: #ffffff;
  }
}

.write-feedback-button {
  &:hover {
    background-color: #112d73;
    box-shadow: inset 0 0 0 9999px rgba(255, 255, 255, 0.08);
  }

  &:active {
    box-shadow: inset 0 0 0 9999px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(17, 45, 115, 0.32);
    outline-offset: 2px;
  }
}
</style>
